import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { DataTable, type Column } from '../../components/DataTable';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { FormRow } from '../../components/ui/FormRow';
import { fmt } from '../../lib/formatters';
import { supabase } from '../../lib/supabase';
import { confirmar } from '../../lib/toast';

type CatalogoRow = {
  id: string;
  ref_proveedor: string;
  nombre: string;
  descripcion: string | null;
  main_specs: string | null;
  coste_usd: number | null;
  moq: number | null;
  lead_time_dias: number | null;
  proveedor_id: string;
  proveedor_nombre: string | null;
  proveedor_foc_pct: number | null;
  foc_pct: number | null;
  gama_id: string | null;
  gama_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  detalle_id: string | null;
  detalle_nombre: string | null;
  sample: 'SOLICITADO' | 'SI' | 'NO' | null;
  envio_tipo: string;
  origen: string | null;
  precio_actual_fecha: string | null;
  precio_actual_usd: number | null;
  precio_actual_unidades: number | null;
  tiene_opciones: boolean;
  activo: boolean;
  created_at: string;
  updated_at: string;
  imagen_storage_path: string | null;
};

export function CatalogoList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const lista = useQuery({
    queryKey: ['catalogo', 'listado-full'],
    queryFn: () => api.get<{ data: CatalogoRow[] }>('/productos-proveedor/listado/full'),
  });
  // Firma en una sola llamada las URLs de las miniaturas para mostrar la
  // imagen principal en el listado. Si Storage no está configurado el
  // backend devuelve null para todas — el render cae a "—".
  const paths = useMemo(
    () => (lista.data?.data ?? [])
      .map((r) => r.imagen_storage_path)
      .filter((p): p is string => Boolean(p)),
    [lista.data],
  );
  const imagenes = useQuery({
    queryKey: ['catalogo', 'firmar-imagenes', paths],
    queryFn: () => api.post<{ urls: Record<string, string | null> }>(
      '/productos-proveedor/firmar-imagenes-bulk',
      { paths },
    ),
    enabled: paths.length > 0,
    staleTime: 30 * 60 * 1000, // 30 min — las URLs duran 1h
  });
  const [zoomFoto, setZoomFoto] = useState<{ url: string; nombre: string } | null>(null);
  const proveedores = useQuery({
    queryKey: ['list', '/proveedores'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/proveedores'),
  });
  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'),
  });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/categorias'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string }[] }>('/admin/subcategorias'),
  });
  const detalles = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string; activo: boolean }[] }>('/admin/detalles'),
  });
  // ── Selección múltiple + bulk update ─────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkGama, setBulkGama] = useState('');
  const [bulkCategoria, setBulkCategoria] = useState('');
  const [bulkSubcategoria, setBulkSubcategoria] = useState('');
  const [bulkDetalle, setBulkDetalle] = useState('');
  const [bulkSample, setBulkSample] = useState('');
  const subcatsBulk = useMemo(() => {
    const all = subcategorias.data?.data ?? [];
    if (!bulkCategoria || bulkCategoria === '__none__') return all;
    return all.filter((s) => s.categoria_id === bulkCategoria);
  }, [bulkCategoria, subcategorias.data]);
  const detallesBulk = useMemo(() => {
    const all = (detalles.data?.data ?? []).filter((d) => d.activo);
    if (!bulkSubcategoria || bulkSubcategoria === '__none__') return all;
    return all.filter((d) => d.subcategoria_id === bulkSubcategoria);
  }, [bulkSubcategoria, detalles.data]);
  const bulkMut = useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      api.patch<{ updated: number }>('/productos-proveedor/bulk', {
        ids: Array.from(selectedIds),
        updates,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setSelectedIds(new Set());
      setBulkGama(''); setBulkCategoria(''); setBulkSubcategoria(''); setBulkDetalle(''); setBulkSample('');
      setImportResult({ ok: true, msg: `Bulk: ${r.updated} producto(s) actualizado(s).` });
    },
    onError: (err) => setImportResult({ ok: false, msg: (err as Error).message }),
  });
  const bulkDeleteMut = useMutation({
    mutationFn: () =>
      api.post<{ deleted: number }>('/productos-proveedor/bulk-delete', {
        ids: Array.from(selectedIds),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setSelectedIds(new Set());
      setImportResult({ ok: true, msg: `${r.deleted} producto(s) eliminado(s).` });
    },
    onError: (err) => setImportResult({ ok: false, msg: (err as Error).message }),
  });
  const applyBulk = async () => {
    const updates: Record<string, unknown> = {};
    if (bulkGama) updates.gama_id = bulkGama === '__none__' ? null : bulkGama;
    if (bulkCategoria) updates.categoria_id = bulkCategoria === '__none__' ? null : bulkCategoria;
    if (bulkSubcategoria) updates.subcategoria_id = bulkSubcategoria === '__none__' ? null : bulkSubcategoria;
    if (bulkDetalle) updates.detalle_id = bulkDetalle === '__none__' ? null : bulkDetalle;
    if (bulkSample) updates.sample = bulkSample === '__none__' ? null : bulkSample;
    if (Object.keys(updates).length === 0) return;
    if (!(await confirmar({ title: `¿Aplicar cambios a ${selectedIds.size} producto(s)?`, confirmLabel: 'Aplicar', danger: true }))) return;
    bulkMut.mutate(updates);
  };
  const numSeleccionados = selectedIds.size;
  const noneOpt = { value: '__none__', label: '— quitar valor —' };

  // ── Cotizar desde catálogo ───────────────────────────────────────
  // Las opciones del selector son MARCAS (no clientes). El backend
  // resuelve el cliente a partir de la marca y la cotización aterriza en
  // la zona del cliente correcto (GAME o TICNOVA).
  const [cotizarPpId, setCotizarPpId] = useState<string | null>(null);
  const [cotizarMarcaId, setCotizarMarcaId] = useState('');
  const [bulkCotizOpen, setBulkCotizOpen] = useState(false);
  const [bulkCotizMarcaId, setBulkCotizMarcaId] = useState('');
  const clientesQ = useQuery({
    queryKey: ['list', '/admin/clientes'],
    queryFn: () => api.get<{ data: { id: string; codigo: string; nombre: string }[] }>('/admin/clientes'),
  });
  const marcasQ = useQuery({
    queryKey: ['list', '/admin/marcas'],
    queryFn: () => api.get<{ data: { id: string; cliente_id: string; codigo: string; nombre: string }[] }>('/admin/marcas'),
  });
  // Marcas agrupadas por cliente para mostrar con <optgroup> y dejar
  // claro que cada item es una MARCA (no un cliente). En GAME ambos se
  // llaman "GAME", lo que era confuso en una lista plana.
  type MarcaOpt = { id: string; nombre: string; clienteCodigo: string };
  const marcasPorCliente = useMemo(() => {
    const cliMap = new Map((clientesQ.data?.data ?? []).map((c) => [c.id, c]));
    const groups = new Map<string, { codigo: string; nombre: string; marcas: MarcaOpt[] }>();
    for (const m of marcasQ.data?.data ?? []) {
      const cli = cliMap.get(m.cliente_id);
      const key = cli?.id ?? 'sin-cliente';
      if (!groups.has(key)) {
        groups.set(key, {
          codigo: cli?.codigo ?? 'Sin cliente',
          nombre: cli?.nombre ?? 'Sin cliente',
          marcas: [],
        });
      }
      groups.get(key)!.marcas.push({
        id: m.id,
        nombre: m.nombre || m.codigo,
        clienteCodigo: cli?.codigo ?? '',
      });
    }
    for (const g of groups.values()) {
      g.marcas.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }
    return Array.from(groups.values()).sort((a, b) => a.codigo.localeCompare(b.codigo, 'es'));
  }, [marcasQ.data, clientesQ.data]);
  // Lookup plano para resolver el cliente de la marca elegida.
  const marcaLookup = useMemo(() => {
    const out = new Map<string, MarcaOpt>();
    for (const g of marcasPorCliente) for (const m of g.marcas) out.set(m.id, m);
    return out;
  }, [marcasPorCliente]);
  const bulkCotizMut = useMutation({
    mutationFn: (marcaId: string) =>
      api.post<{ created: number }>('/precio/cotizaciones/bulk-crear', {
        producto_proveedor_ids: Array.from(selectedIds),
        marca_id: marcaId,
      }),
    onSuccess: (r) => {
      setSelectedIds(new Set());
      setBulkCotizOpen(false);
      setBulkCotizMarcaId('');
      setImportResult({ ok: true, msg: `${r.created} cotización(es) creada(s). Marcadas como Pdte. de revisar.` });
    },
    onError: (err) => setImportResult({ ok: false, msg: (err as Error).message }),
  });

  // ── Importación XLSX ─────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importResult, setImportResult] = useState<{
    ok: boolean;
    msg: string;
    detail?: Record<string, unknown>;
  } | null>(null);

  // ── Importar ficha de proveedor (un XLSX = un producto) ─────────
  const [fichaModal, setFichaModal] = useState(false);
  const [fichaProveedor, setFichaProveedor] = useState<string>('');
  const [fichaFile, setFichaFile] = useState<File | null>(null);

  const importarFicha = useMutation({
    mutationFn: async ({ file, proveedor_id }: { file: File; proveedor_id: string }) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('proveedor_id', proveedor_id);
      const res = await fetch('/api/productos-proveedor/import-ficha', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((body as { message?: string; error?: string }).message ?? `Import falló (${res.status})`);
      }
      const { id } = body as { id: string; detected: Record<string, unknown> };
      // Adjuntar el propio XLSX como ficha del producto recién creado.
      const fd2 = new FormData();
      fd2.append('file', file);
      fd2.append('tipo', 'ficha');
      await fetch(`/api/productos-proveedor/${id}/archivos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd2,
      }).catch((err) => {
        console.warn('Subir XLSX como ficha falló (producto ya creado):', err);
      });
      return { id, detected: (body as { detected: Record<string, unknown> }).detected };
    },
    onSuccess: ({ id, detected }) => {
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setFichaModal(false);
      setFichaProveedor('');
      setFichaFile(null);
      setImportResult({
        ok: true,
        msg: `Producto creado. Detectado: ${JSON.stringify(detected)}`,
      });
      navigate(`/proveedores/productos/${id}`);
    },
    onError: (err) => setImportResult({ ok: false, msg: (err as Error).message }),
  });

  const importarXlsx = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/productos-proveedor/import-xlsx', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((body as { message?: string; error?: string }).message
          ?? (body as { error?: string }).error
          ?? `Import falló (${res.status})`);
      }
      return body as {
        created: Record<string, number>;
        skipped: Array<{ row: number; reason: string }>;
        total_filas: number;
      };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setImportResult({
        ok: true,
        msg: `Importadas ${r.created.productos} filas · ${r.skipped.length} saltadas de ${r.total_filas}`,
        detail: r as unknown as Record<string, unknown>,
      });
    },
    onError: (err) => setImportResult({ ok: false, msg: (err as Error).message }),
  });

  const columns: Column<CatalogoRow>[] = [
    {
      key: 'created_at',
      label: 'Fecha creación',
      render: (r) => <span className="text-xs text-slate-500">{fmt.date(r.created_at)}</span>,
      sortValue: (r) => r.created_at,
    },
    {
      key: 'origen',
      label: 'Origen',
      render: (r) => r.origen ? <span className="text-xs">{r.origen}</span> : <span className="text-xs text-slate-300">—</span>,
      sortValue: (r) => r.origen ?? '',
    },
    { key: 'proveedor_nombre', label: 'Proveedor', sortValue: (r) => r.proveedor_nombre ?? '' },
    {
      key: 'ref_proveedor',
      label: 'Ref',
      render: (r) => <span className="font-mono text-xs">{r.ref_proveedor}</span>,
      sortValue: (r) => r.ref_proveedor,
    },
    {
      key: '_foto',
      label: 'Foto',
      sortable: false,
      width: '4rem',
      render: (r) => {
        const url = r.imagen_storage_path ? imagenes.data?.urls?.[r.imagen_storage_path] : null;
        if (!url) return <span className="text-slate-300">—</span>;
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomFoto({ url, nombre: r.nombre }); }}
            className="block h-8 w-8 cursor-pointer overflow-hidden rounded border border-slate-200 bg-white hover:border-slate-400"
            title="Ver foto"
          >
            <img src={url} alt={r.nombre} className="h-full w-full object-cover" loading="lazy" />
          </button>
        );
      },
    },
    { key: 'nombre', label: 'Nombre', sortValue: (r) => r.nombre },
    { key: 'gama_nombre', label: 'Gama', sortValue: (r) => r.gama_nombre ?? '' },
    { key: 'subcategoria_nombre', label: 'Subtipo', sortValue: (r) => r.subcategoria_nombre ?? '' },
    {
      key: 'detalle_nombre',
      label: 'Detalle',
      render: (r) => r.detalle_nombre ?? <span className="text-slate-300">—</span>,
      sortValue: (r) => r.detalle_nombre ?? '',
    },
    {
      key: 'sample',
      label: 'Sample',
      render: (r) => {
        if (!r.sample) return <span className="text-slate-300">—</span>;
        const colors: Record<string, string> = {
          SOLICITADO: '#f59e0b',
          SI: '#10b981',
          NO: '#94a3b8',
        };
        return <Badge color={colors[r.sample] ?? '#94a3b8'}>{r.sample}</Badge>;
      },
      sortValue: (r) => r.sample ?? '',
    },
    {
      key: 'precio_actual_usd',
      label: 'Precio',
      align: 'right',
      render: (r) => (
        <div className="text-xs">
          <div className="font-mono">{fmt.usd(r.precio_actual_usd)}</div>
          {r.precio_actual_unidades != null && <div className="text-slate-400">@ {fmt.int(r.precio_actual_unidades)} ud</div>}
        </div>
      ),
      sortValue: (r) => Number(r.precio_actual_usd),
    },
    {
      key: 'moq',
      label: 'MOQ',
      align: 'right',
      render: (r) => fmt.number(r.moq),
      sortValue: (r) => Number(r.moq),
    },
    {
      key: 'foc_pct',
      label: 'F.O.C.',
      align: 'right',
      render: (r) => {
        const efectivo = r.foc_pct ?? r.proveedor_foc_pct ?? null;
        if (efectivo == null) return <span className="text-slate-300">—</span>;
        return (
          <span title={r.foc_pct == null ? 'Heredado del proveedor' : 'Override'} className="text-xs">
            {fmt.pct(efectivo)}
            {r.foc_pct == null && <em className="ml-1 text-[10px] text-slate-400">(prov)</em>}
          </span>
        );
      },
      sortValue: (r) => Number(r.foc_pct ?? r.proveedor_foc_pct ?? 0),
    },
    {
      key: 'tiene_opciones',
      label: 'Opciones',
      render: (r) => r.tiene_opciones
        ? <Badge color="#10b981">SÍ</Badge>
        : <span className="text-xs text-slate-400">no</span>,
      sortValue: (r) => (r.tiene_opciones ? 1 : 0),
    },
    {
      key: '_cotizar',
      label: '',
      sortable: false,
      width: '5rem',
      render: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCotizarPpId(r.id); setCotizarMarcaId(''); }}
          className="cursor-pointer rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
        >
          Cotizar
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Productos de proveedor"
        subtitle="Catálogo de productos ofertados por los proveedores con histórico de precios e info logística."
        actions={
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImportResult(null);
                  importarXlsx.mutate({ file });
                }
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={importarXlsx.isPending}
              title="Importación masiva desde un Excel con varias filas. Salta filas vacías o con ref duplicada."
            >
              {importarXlsx.isPending ? 'Importando…' : '⬆️ Importación masiva XLSX'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setFichaModal(true)}
              disabled={importarFicha.isPending}
              title="Sube una ficha de proveedor (Excel de un solo producto). El sistema extrae nombre/MOQ/precio/specs y deja el XLSX adjunto."
            >
              {importarFicha.isPending ? 'Importando…' : '📋 Importar ficha proveedor'}
            </Button>
            <Button variant="create" onClick={() => navigate('/proveedores/productos/nuevo')}>
              + Añadir producto
            </Button>
          </>
        }
      />

      {importResult && (
        <div className={`mb-3 rounded-md border px-3 py-2 text-sm ${importResult.ok
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
          : 'border-rose-300 bg-rose-50 text-rose-800'}`}>
          <strong className="font-semibold">{importResult.ok ? '✓ Import completado:' : '✗ Import falló:'}</strong>{' '}
          {importResult.msg}
          {importResult.detail && (
            <details className="mt-1 text-xs">
              <summary className="cursor-pointer">Detalle</summary>
              <pre className="mt-1 overflow-auto rounded bg-white/60 p-2">{JSON.stringify(importResult.detail, null, 2)}</pre>
            </details>
          )}
          <button onClick={() => setImportResult(null)} className="ml-2 cursor-pointer text-xs underline">cerrar</button>
        </div>
      )}

      {numSeleccionados > 0 && (
        <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-semibold text-sky-900">
              {numSeleccionados} producto{numSeleccionados === 1 ? '' : 's'} seleccionado{numSeleccionados === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
            >
              Limpiar selección
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-9">
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Gama</label>
              <Select
                options={[noneOpt, ...(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))]}
                value={bulkGama}
                onChange={(e) => setBulkGama(e.target.value)}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Categoría</label>
              <Select
                options={[noneOpt, ...(categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))]}
                value={bulkCategoria}
                onChange={(e) => { setBulkCategoria(e.target.value); setBulkSubcategoria(''); setBulkDetalle(''); }}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Subtipo</label>
              <Select
                options={[noneOpt, ...subcatsBulk.map((s) => ({ value: s.id, label: s.nombre }))]}
                value={bulkSubcategoria}
                onChange={(e) => { setBulkSubcategoria(e.target.value); setBulkDetalle(''); }}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Detalle</label>
              <Select
                options={[noneOpt, ...detallesBulk.map((d) => ({ value: d.id, label: d.nombre }))]}
                value={bulkDetalle}
                onChange={(e) => setBulkDetalle(e.target.value)}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Sample</label>
              <Select
                options={[
                  noneOpt,
                  { value: 'SOLICITADO', label: 'Solicitado' },
                  { value: 'SI', label: 'Sí' },
                  { value: 'NO', label: 'No' },
                ]}
                value={bulkSample}
                onChange={(e) => setBulkSample(e.target.value)}
                emptyLabel="(sin cambio)"
              />
            </div>
            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
              <Button
                variant="save"
                onClick={applyBulk}
                disabled={bulkMut.isPending}
                title="Aplicar los cambios a los productos seleccionados"
                className="flex-1 whitespace-nowrap"
              >
                {bulkMut.isPending ? 'Aplicando…' : 'Aplicar'}
              </Button>
              <Button
                variant="create"
                onClick={() => { setBulkCotizOpen(true); setBulkCotizMarcaId(''); }}
                className="flex-1 whitespace-nowrap"
              >
                Cotización masiva
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (!(await confirmar({ title: `¿Eliminar ${numSeleccionados} producto(s)?`, message: 'Se podrán recuperar desde base de datos.', confirmLabel: 'Eliminar', danger: true }))) return;
                  bulkDeleteMut.mutate();
                }}
                disabled={bulkDeleteMut.isPending}
                className="flex-1 whitespace-nowrap"
              >
                {bulkDeleteMut.isPending ? 'Eliminando…' : '🗑 Eliminar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <DataTable<CatalogoRow>
        columns={columns}
        rows={lista.data?.data}
        loading={lista.isLoading}
        rowKey={(r) => r.id}
        searchPlaceholder="Buscar en catálogo…"
        defaultSort={{ key: 'created_at', desc: true }}
        onRowClick={(r) => navigate(`/proveedores/productos/${r.id}`)}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        filters={[
          { key: 'proveedor_id', label: 'Proveedor', options: (proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre })) },
          { key: 'gama_id', label: 'Gama', options: (gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre })) },
          { key: 'categoria_id', label: 'Categoría', options: (categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre })) },
          { key: 'subcategoria_id', label: 'Subtipo', options: [...(subcategorias.data?.data ?? [])].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')).map((s) => ({ value: s.id, label: s.nombre })) },
          { key: 'detalle_id', label: 'Detalle', options: [...(detalles.data?.data ?? [])].filter((d) => d.activo).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')).map((d) => ({ value: d.id, label: d.nombre })) },
          {
            key: 'specs',
            label: 'Buscar en specs',
            type: 'text' as const,
            placeholder: 'Texto en specs/descr.',
            getValue: (r: CatalogoRow) => `${r.main_specs ?? ''}\n${r.descripcion ?? ''}`,
          },
          {
            key: 'precio_actual_usd',
            label: 'Rango precio (USD)',
            type: 'numberRange' as const,
            getValue: (r: CatalogoRow) => {
              const n = Number(r.precio_actual_usd);
              return Number.isFinite(n) && n > 0 ? n : null;
            },
          },
        ]}
      />

      {/* Modal Importar ficha proveedor */}
      <Modal
        open={fichaModal}
        onClose={() => { setFichaModal(false); setFichaProveedor(''); setFichaFile(null); }}
        title="Importar ficha de proveedor"
        size="md"
        footer={
          <>
            <Button variant="danger" onClick={() => { setFichaModal(false); setFichaProveedor(''); setFichaFile(null); }}>Cancelar</Button>
            <Button
              variant="create"
              onClick={() => fichaFile && importarFicha.mutate({ file: fichaFile, proveedor_id: fichaProveedor })}
              disabled={!fichaProveedor || !fichaFile || importarFicha.isPending}
            >
              {importarFicha.isPending ? 'Subiendo…' : 'Subir y crear producto'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Sube un Excel con la ficha de UN solo producto. El sistema intentará extraer nombre,
            MOQ, precio y specs por heurística, creará el producto y dejará el propio XLSX
            como adjunto tipo &quot;ficha&quot;. Después puedes editar la ficha del producto.
          </p>
          <FormRow label="Proveedor" required>
            <Select
              options={(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))}
              value={fichaProveedor}
              emptyLabel="— seleccionar —"
              onChange={(e) => setFichaProveedor(e.target.value)}
            />
          </FormRow>
          <FormRow label="Archivo Excel" required>
            <input
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFichaFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm"
            />
          </FormRow>
          {fichaFile && (
            <p className="text-xs text-slate-500">
              <strong>{fichaFile.name}</strong> · {(fichaFile.size / 1024).toFixed(1)} KB
              {fichaFile.size > 2 * 1024 * 1024 && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                  ⚠ Mayor a 2 MB
                </span>
              )}
            </p>
          )}
        </div>
      </Modal>

      {/* Modal Cotización masiva — selección de MARCA */}
      <Modal
        open={bulkCotizOpen}
        onClose={() => setBulkCotizOpen(false)}
        title={`Cotización masiva — ${numSeleccionados} producto(s)`}
        size="sm"
        footer={
          <>
            <Button variant="danger" onClick={() => setBulkCotizOpen(false)}>Cancelar</Button>
            <Button
              variant="create"
              disabled={!bulkCotizMarcaId || bulkCotizMut.isPending}
              onClick={() => bulkCotizMut.mutate(bulkCotizMarcaId)}
            >
              {bulkCotizMut.isPending ? 'Creando…' : `Crear ${numSeleccionados} cotización(es)`}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          Se crearán <strong>{numSeleccionados}</strong> cotizaciones con los datos heredados de cada producto
          (coste, MOQ, FOC, subcategoría, condiciones comerciales de la marca elegida).
          Quedarán marcadas como <strong>⚠️ Pdte. de revisar</strong> en la zona del cliente al que pertenece la marca.
        </p>
        <label className="mb-1 block text-xs font-medium text-slate-600">¿A qué marca cotizar?</label>
        <select
          value={bulkCotizMarcaId}
          onChange={(e) => setBulkCotizMarcaId(e.target.value)}
          className="block w-full cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        >
          <option value="">— elegir marca —</option>
          {marcasPorCliente.map((g) => (
            <optgroup key={g.codigo} label={`Cliente ${g.codigo}`}>
              {g.marcas.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </Modal>

      {/* Modal Cotizar individual — selección de MARCA + abre calculadora */}
      <Modal
        open={!!cotizarPpId}
        onClose={() => setCotizarPpId(null)}
        title="¿Para qué marca cotizar?"
        size="sm"
        footer={
          <>
            <Button variant="danger" onClick={() => setCotizarPpId(null)}>Cancelar</Button>
            <Button
              variant="create"
              disabled={!cotizarMarcaId}
              onClick={() => {
                const opt = marcaLookup.get(cotizarMarcaId);
                const base = opt?.clienteCodigo === 'TICNOVA' ? '/ticnova/calculadora' : '/game/calculadora';
                navigate(`${base}?pp=${cotizarPpId}&marca=${cotizarMarcaId}`);
                setCotizarPpId(null);
              }}
            >
              Cotizar
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          Selecciona la marca para aplicar las condiciones comerciales correspondientes.
        </p>
        <label className="mb-1 block text-xs font-medium text-slate-600">¿A qué marca cotizar?</label>
        <select
          value={cotizarMarcaId}
          onChange={(e) => setCotizarMarcaId(e.target.value)}
          className="block w-full cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        >
          <option value="">— elegir marca —</option>
          {marcasPorCliente.map((g) => (
            <optgroup key={g.codigo} label={`Cliente ${g.codigo}`}>
              {g.marcas.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </Modal>

      {/* Zoom de miniatura: muestra la foto principal en grande sin
          abandonar el listado. */}
      <Modal
        open={!!zoomFoto}
        onClose={() => setZoomFoto(null)}
        title={zoomFoto?.nombre ?? ''}
        size="lg"
      >
        {zoomFoto && (
          <img
            src={zoomFoto.url}
            alt={zoomFoto.nombre}
            className="mx-auto max-h-[70vh] w-auto rounded border border-slate-200 object-contain"
          />
        )}
      </Modal>

    </div>
  );
}
